import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getBillingAnalysisMarketCodeGroups } from 'Chores/request';

// Holds user-facing state shared between the nav bar and the Billing Analysis
// page. `selectedMarketCodes` is the list of market codes the user picked from
// the market-code popup; it is reset to `[]` on login and on logout, and
// drives both the popup gating logic (in Layout) and the dropdown selection
// (in the Billing Analysis page).
//
// `marketCodeOptions` caches the result of the market-code-groups API so the
// dropdown and the popup don't have to refetch it on every mount. The cache
// survives navigation; the next login session re-fetches via the same thunk.
export const fetchUserMarketCodes = createAsyncThunk(
  'billingAnalysis/fetchUserMarketCodes',
  async () => {
    const res = await getBillingAnalysisMarketCodeGroups();
    const groups = res?.groups || [];
    // Flatten grouped response into the flat option list consumed by
    // the dropdown/popup components.
    return groups.flatMap((g) =>
      (g.marketCodes || []).map((mc) => ({
        id: mc.code,
        displayName: `${g.buName} (${mc.code})`,
        dictDataId: mc.dictDataId || '',
        buName: g.buName || ''
      }))
    );
  },
  {
    condition: (_, { getState }) => {
      const status = getState().billingAnalysis?.marketCodeOptionsStatus;
      // Skip when already loading or successfully cached.
      if (status === 'loading' || status === 'succeeded') return false;
      return true;
    }
  }
);

export const billingAnalysisSlice = createSlice({
  name: 'billingAnalysis',
  initialState: {
    selectedMarketCodes: [],
    marketCodeOptions: [],
    marketCodeOptionsStatus: 'idle'
  },
  reducers: {
    setBillingMarketCodes: (state, action) => {
      state.selectedMarketCodes = Array.isArray(action.payload) ? action.payload : [];
    },
    clearBillingMarketCodes: (state) => {
      state.selectedMarketCodes = [];
      state.marketCodeOptions = [];
      state.marketCodeOptionsStatus = 'idle';
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserMarketCodes.pending, (state) => {
        state.marketCodeOptionsStatus = 'loading';
      })
      .addCase(fetchUserMarketCodes.fulfilled, (state, action) => {
        state.marketCodeOptionsStatus = 'succeeded';
        state.marketCodeOptions = action.payload || [];
      })
      .addCase(fetchUserMarketCodes.rejected, (state) => {
        state.marketCodeOptionsStatus = 'failed';
      });
  }
});

export const {
  setBillingMarketCodes,
  clearBillingMarketCodes
} = billingAnalysisSlice.actions;

export default billingAnalysisSlice.reducer;
